/**
 * Clean modes: Vacuum, Sweep, Dust-mop and Scrub.
 *
 * FOUR TASK TYPES TO THE OPERATOR, ONE CATEGORY ON THE WIRE. Everywhere
 * a type shows -- the dispatch form, the Type column and its filter, the
 * CSV, the drawer, favourites, the calendar's lanes and the metrics
 * breakdowns -- Vacuum, Sweep, Dust-mop and Scrub are separate types
 * (Akash, 2026-10-22). Underneath, the v1 category of all four is `clean`; the mode rides on the clean's `perform_action`
 * phase as `params.mode` (`backend/v1_contract/v1_contract/schemas.py`,
 * `CLEAN_MODES`). So everything keyed on the category -- the calendar's
 * Cleaning lane, metrics, clean verification, the zone history -- keeps
 * treating them as one, and only the places that NAME a task say which.
 *
 * ABSENT MEANS VACUUM. Every schedule, favourite and API caller from
 * before modes existed sends no mode and gets a vacuum, and a clean
 * whose mode cannot be found (one finished before modes existed, or one
 * RMF originated) is shown as one.
 *
 * Neutral names only: the vendor's own mode names are the edge bridge's
 * to map, and never appear on the platform side.
 *
 * `wet_scrub` IS AN OLD NAME FOR `scrub` (live for a day, worker 0.4.35).
 * Stored schedules, favourites, requests and history may carry it, so
 * every reader here folds it in (`toCleanMode`); nothing writes it.
 */
import type { TaskCatalogEntry, TaskPhase } from '../../api/v1';

export type CleanMode = 'vacuum' | 'sweep' | 'dust_mop' | 'scrub';

export const CLEAN_MODES: readonly CleanMode[] = ['vacuum', 'sweep', 'dust_mop', 'scrub'];

/** Old names, read as the mode each became. Never written. */
export const CLEAN_MODE_ALIASES: Readonly<Record<string, CleanMode>> = { wet_scrub: 'scrub' };

export const DEFAULT_CLEAN_MODE: CleanMode = 'vacuum';

/** The task-type names an operator picks and reads. */
export const CLEAN_MODE_LABEL: Record<CleanMode, string> = {
  vacuum: 'Vacuum',
  sweep: 'Sweep',
  dust_mop: 'Dust-mop',
  scrub: 'Scrub',
};

export function isCleanMode(value: unknown): value is CleanMode {
  return typeof value === 'string' && (CLEAN_MODES as readonly string[]).includes(value);
}

/** A stored or published mode as one of `CLEAN_MODES`, an old name
 *  (`wet_scrub`) folded in; undefined when it is not a mode. Every READ
 *  goes through this. */
export function toCleanMode(value: unknown): CleanMode | undefined {
  if (typeof value !== 'string') return undefined;
  const mode = CLEAN_MODE_ALIASES[value] ?? value;
  return isCleanMode(mode) ? mode : undefined;
}

/**
 * The modes a site offers, from its catalog's clean entry, in the
 * site's order. An entry without `modes` (an older worker or
 * api-server) offers vacuum only -- the one mode every site has. Names
 * this console does not know are dropped rather than offered: it could
 * not label them, and the worker refuses a mode it does not offer.
 */
export function offeredCleanModes(entry: TaskCatalogEntry | undefined): CleanMode[] {
  const known = (entry?.modes ?? [])
    .map(toCleanMode)
    .filter((m): m is CleanMode => m !== undefined);
  return known.length > 0 ? [...new Set(known)] : [DEFAULT_CLEAN_MODE];
}

type PhaseLike = Pick<TaskPhase, 'kind' | 'params'>;

/**
 * The mode a dispatch REQUEST asks for -- a stored request, a schedule's
 * task or a favourite. Reads the clean's own `perform_action` phase (the
 * one whose `params.action` is `clean`), exactly as the worker does.
 * Undefined for anything that is not a clean; vacuum for a clean naming
 * no mode, or one this console does not know.
 */
export function cleanModeOfRequest(
  request:
    | { readonly category?: string; readonly phases?: readonly PhaseLike[] }
    | null
    | undefined,
): CleanMode | undefined {
  if (!request || request.category !== 'clean') return undefined;
  const phase = (request.phases ?? []).find(
    (p) => p.kind === 'perform_action' && p.params?.action === 'clean',
  );
  return toCleanMode(phase?.params?.mode) ?? DEFAULT_CLEAN_MODE;
}

/**
 * The mode a LIVE task's projection carries: the worker stamps it onto
 * a clean's phases as `params.clean_mode`, from the dispatch label RMF
 * round-trips. Undefined when no phase carries one -- a queued task,
 * which publishes no phases, or a clean from before modes existed.
 */
export function cleanModeOfTask(task: {
  readonly phases?: readonly Pick<TaskPhase, 'params'>[];
}): CleanMode | undefined {
  for (const phase of task.phases ?? []) {
    const mode = toCleanMode(phase.params?.clean_mode);
    if (mode) return mode;
  }
  return undefined;
}

/** How a mode reads where types are lower-case tokens (the Type column,
 *  its filter, the CSV, the drawer, calendar titles): `dust-mop`, not
 *  `dust_mop`. */
export const CLEAN_MODE_TEXT: Record<CleanMode, string> = {
  vacuum: 'vacuum',
  sweep: 'sweep',
  dust_mop: 'dust-mop',
  scrub: 'scrub',
};

/** Task types whose name changed while their v1 category did not: the
 *  `dustbag` category is the "Routine check" (Akash, 2026-10-22) -- the
 *  robot parks at a maintenance point for staff to empty the dustbag and
 *  check the brushes. */
const RENAMED_TYPE_TEXT: Readonly<Record<string, string>> = { dustbag: 'routine-check' };

/** The operator-facing NAME of the `dustbag` category, wherever a label
 *  (not a token) shows: the form, favourites, the calendar legend. */
export const ROUTINE_CHECK_LABEL = 'Routine check';

/**
 * A task's TYPE KEY: its category, except that a clean is its mode
 * (`vacuum` / `sweep` / `dust_mop` / `scrub`) -- vacuum when the mode is unknown,
 * because that is what every clean was before modes existed. The value
 * the Type filter, the CSV and the calendar lanes are keyed by.
 */
export function taskTypeKey(type: string, mode: CleanMode | undefined | null): string {
  if (type !== 'clean') return type;
  return mode ?? DEFAULT_CLEAN_MODE;
}

/** How a type key reads on screen: `dust-mop` for `dust_mop`,
 *  `routine-check` for `dustbag`, everything else as it is. */
export function typeKeyText(key: string): string {
  const mode = toCleanMode(key);
  if (mode) return CLEAN_MODE_TEXT[mode];
  return RENAMED_TYPE_TEXT[key] ?? key;
}

/**
 * The Type a task list, drawer or calendar block shows.
 *
 * A clean shows its MODE, in the same lower-case token style as every
 * other value in those places (`patrol`, `charge`): `vacuum`, `sweep`,
 * `dust-mop`, `scrub`. A dustbag task reads `routine-check`. Everything
 * else is shown exactly as before.
 */
export function taskTypeText(type: string, mode: CleanMode | undefined | null): string {
  return typeKeyText(taskTypeKey(type, mode));
}

/**
 * A LIVE clean's mode, from what this console already has: the mode the
 * worker stamped on its phases, else the dispatch request it has
 * already read (`cached`, as `readCachedTaskRequest` returns it).
 *
 * Undefined: not a clean. `null`: a clean whose mode is not known YET --
 * no phases (queued) and no request read so far. A clean WITH phases and
 * no stamp predates modes and is a vacuum; so is one whose request is
 * not on file (`cached === null`).
 */
export function liveCleanMode(
  task: { readonly type: string; readonly phases?: readonly Pick<TaskPhase, 'params'>[] },
  cached: { readonly request?: Parameters<typeof cleanModeOfRequest>[0] } | null | undefined,
): CleanMode | null | undefined {
  if (task.type !== 'clean') return undefined;
  const stamped = cleanModeOfTask(task);
  if (stamped) return stamped;
  if ((task.phases?.length ?? 0) > 0 || cached === null) return DEFAULT_CLEAN_MODE;
  if (cached === undefined) return null;
  return cleanModeOfRequest(cached.request) ?? DEFAULT_CLEAN_MODE;
}

/** "Vacuum" / "Sweep" / "Dust-mop" / "Scrub"; "Vacuum" when unknown. */
export function cleanModeLabel(mode: CleanMode | undefined | null): string {
  return CLEAN_MODE_LABEL[mode ?? DEFAULT_CLEAN_MODE];
}
